'use client'

import { useEffect, useState } from 'react'

/**
 * Focus moving into an in-document iframe (e.g. Pylon) blurs the parent
 * `window` without the app losing focus, so `document.hasFocus()` — which stays
 * true for iframe focus — is the signal to trust. A hidden document is never
 * focused, whatever it last reported.
 */
const isDocumentFocused = () => document.visibilityState === 'visible' && document.hasFocus()

export const useIsWindowFocused = () => {
  const [isFocused, setIsFocused] = useState(isDocumentFocused)

  useEffect(() => {
    let pendingSync = 0
    const syncFromDocument = () => {
      setIsFocused(isDocumentFocused())
    }
    // Focus lands after the event that announced it, so let it settle first.
    const syncOnNextFrame = () => {
      cancelAnimationFrame(pendingSync)
      pendingSync = requestAnimationFrame(syncFromDocument)
    }
    const onWindowFocus = () => {
      setIsFocused(true)
    }
    const onVisibilityChange = () => {
      // Once focus has entered an iframe, leaving the app emits no further
      // parent-window blur. Visibility is document-level, so it still observes
      // real deactivation without mistaking iframe focus for it.
      if (document.visibilityState === 'hidden') {
        // Apply straight away: animation frames are paused while hidden.
        cancelAnimationFrame(pendingSync)
        setIsFocused(false)
        return
      }
      // Re-sync on the way back too. The parent window emits no `focus` when
      // the document returns with focus still in an iframe, so without this the
      // hidden state latches: the traffic lights stay dim, and consumers that
      // watch for the focused→unfocused edge never see another one.
      syncOnNextFrame()
    }
    window.addEventListener('focus', onWindowFocus)
    window.addEventListener('blur', syncOnNextFrame)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      cancelAnimationFrame(pendingSync)
      window.removeEventListener('focus', onWindowFocus)
      window.removeEventListener('blur', syncOnNextFrame)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [setIsFocused])

  return isFocused
}
